import { motion } from "motion/react";
import {
    LayoutDashboard,
    Wallet,
    LineChart,
    Settings,
    ShieldCheck,
    Bell,
    Building2,
    Users
} from "lucide-react";
import { useState } from "react";

import NotificationsPanel from "../components/notifications-panel";
import { useRWAStore } from "../store/rwa-store";

interface LayoutShellProps {
    children: React.ReactNode;
    activeTab: string;
    onNavigate: (tab: string) => void;
    onExit: () => void;
}

export default function LayoutShell({ children, activeTab, onNavigate, onExit: _onExit }: LayoutShellProps) {
    const { currentUser, logout, notifications } = useRWAStore();
    const [showNotifications, setShowNotifications] = useState(false);
    const unreadCount = notifications.filter(n => !n.read).length;

    if (!currentUser) return null;

    const _handleLogout = () => {
        logout();
        // onExit is handled by the parent observing auth state usually, 
        // but explicit call handles the 'Landing' transition if needed.
    };

    const MENU_ITEMS = [
        { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, role: 'all' },
        { id: 'marketplace', label: 'Marketplace', icon: Building2, role: 'all' },
        { id: 'portfolio', label: 'Portfolio', icon: LineChart, role: 'investor' },
        { id: 'wallet', label: 'Wallet', icon: Wallet, role: 'all' },
        { id: 'admin-users', label: 'User Mgmt', icon: Users, role: 'admin' },
        { id: 'settings', label: 'Settings', icon: Settings, role: 'all' },
    ];

    const filteredMenu = MENU_ITEMS.filter(item =>
        item.role === 'all' ||
        item.role === currentUser.role ||
        (currentUser.role === 'admin' && item.role === 'issuer') // Admins see issuer stuff too
    );

    return (
        <div className="flex h-screen bg-neutral-950 text-white overflow-hidden font-sans">
            {/* --- SIDEBAR --- */}
            <motion.aside
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                className="w-64 flex flex-col bg-[#050505] border-r border-white/5"
            >
                <div className="p-8 flex items-center gap-3">
                    <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center">
                        <ShieldCheck className="w-5 h-5 text-lime-400" />
                    </div>
                    <div className="font-bold tracking-tight text-white select-none">RWA Terminal</div>
                </div>

                <nav className="flex-1 px-4 space-y-2">
                    {filteredMenu.map((item) => (
                        <button
                            key={item.id}
                            onClick={() => onNavigate(item.id)}
                            className={`relative w-full flex items-center gap-4 px-4 py-3 text-sm font-medium transition-all duration-300 rounded-lg group ${activeTab === item.id
                                ? 'text-lime-400 bg-white/5'
                                : 'text-stone-500 hover:text-stone-200 hover:bg-white/5'
                                }`}
                        >
                            <item.icon className="w-4 h-4 transition-colors group-hover:text-white" />
                            {item.label}
                            {item.id === 'wallet' && (
                                <span className="absolute right-4 w-1.5 h-1.5 rounded-full bg-lime-400/50"></span>
                            )}
                        </button>
                    ))}

                    <div className="pt-8 pb-4 pl-4 text-xs font-bold text-stone-600 uppercase tracking-widest">Pocket</div>
                    <button className="w-full flex items-center gap-4 px-4 py-3 text-sm font-medium text-stone-500 hover:text-stone-200 hover:bg-white/5 rounded-lg transition-all">
                        <Wallet className="w-4 h-4" /> Staked Rewards
                    </button>
                </nav>

                <div className="p-6 mt-auto">
                    <div className="bg-[#111111] rounded-xl p-3 border border-white/5">
                        <div className="text-[10px] uppercase text-stone-500 font-bold mb-2 tracking-wider">Dev Control</div>
                        <div className="space-y-2">
                            {useRWAStore.getState().users.map(u => (
                                <button
                                    key={u.id}
                                    onClick={() => useRWAStore.getState().switchUser(u.id)}
                                    className={`w-full text-left text-xs py-1.5 px-2 rounded flex items-center justify-between group ${currentUser.id === u.id ? 'bg-white/10 text-white' : 'text-stone-500 hover:bg-white/5'}`}
                                >
                                    <span>{u.name}</span>
                                    <span className="opacity-0 group-hover:opacity-100 uppercase text-[9px] border border-stone-600 px-1 rounded">{u.role}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </motion.aside>

            {/* --- MAIN CONTENT --- */}
            <main className="flex-1 flex flex-col min-w-0">
                <header className="h-16 border-b border-neutral-900 flex items-center justify-between px-6 bg-neutral-950/80 backdrop-blur-md z-10 sticky top-0">
                    <h2 className="text-lg font-medium capitalize text-neutral-200">
                        {filteredMenu.find(m => m.id === activeTab)?.label || 'Overview'}
                    </h2>

                    <div className="flex items-center gap-6">
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-neutral-900 rounded-full border border-neutral-800">
                            <div className={`w-2 h-2 rounded-full ${currentUser.kycStatus === 'verified' ? 'bg-emerald-500' : 'bg-amber-500 animate-pulse'}`} />
                            <span className="text-xs font-mono text-neutral-400 uppercase">{currentUser.kycStatus}</span>
                        </div>

                        <div className="relative">
                            <button
                                onClick={() => setShowNotifications(!showNotifications)}
                                className="relative p-2 hover:bg-neutral-800 rounded-lg transition-colors"
                            >
                                <Bell className="w-5 h-5 text-neutral-400 hover:text-white transition-colors" />
                                {unreadCount > 0 && (
                                    <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                                        {unreadCount > 9 ? '9+' : unreadCount}
                                    </span>
                                )}
                            </button>
                            <NotificationsPanel
                                isOpen={showNotifications}
                                onClose={() => setShowNotifications(false)}
                            />
                        </div>

                        <div className="flex items-center gap-3 pl-6 border-l border-neutral-800">
                            <div className="text-right hidden md:block">
                                <div className="text-sm font-medium text-white">{currentUser.name}</div>
                                <div className="text-xs text-neutral-500 capitalize">{currentUser.role}</div>
                            </div>
                            <div className="w-8 h-8 rounded-full bg-linear-to-tr from-emerald-500 to-cyan-500 border border-white/10" />
                        </div>
                    </div>
                </header>

                <div className="flex-1 overflow-y-auto p-6 md:p-8">
                    <motion.div
                        key={activeTab}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="h-full"
                    >
                        {children}
                    </motion.div>
                </div>
            </main>
        </div>
    );
}
